/**
 * PhotoViewer — full-screen viewer for a single photo, with prev/next nav.
 * FT-343 (2026-07-08). See DESIGN-BRIEF.md §1.2. Loads full blob on demand,
 * revokes URL on close/photo change/unmount. Swipe + arrow-key + button nav.
 * Legacy base64 strings render directly. WebKit multi-touch garbage guarded.
 * FT-343 pre-merge: stable-id effect key, input-safe swipe zone, corrupt-blob
 * onError banner with retry. FT-349d (2026-07-09): Replace button.
 */

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Camera, ChevronLeft, ChevronRight, Trash2, Loader2, AlertTriangle } from 'lucide-react';
import { Modal, ModalBody, ModalActions } from '../../tailwind/common/Modal';
import { PhotoCaptionInput } from './PhotoCaptionInput';

const SWIPE_MIN_PX = 60;
// Elements that must NEVER trigger a swipe (caption input, buttons, etc.).
const SWIPE_IGNORE_SELECTOR = 'input, textarea, button, [data-no-swipe]';

/**
 * Props: isOpen, index, onIndexChange(idx), onClose, photos (PhotoRef|string)[],
 * store, caption?, onCaptionSave?(idx,next), onRequestDelete?(idx),
 * onRequestReplace?(idx) [FT-349d], disabled?.
 */
export function PhotoViewer({
  isOpen,
  index,
  onIndexChange,
  onClose,
  photos,
  store,
  caption = false,
  onCaptionSave,
  onRequestDelete,
  onRequestReplace,
  disabled = false,
}) {
  const [url, setUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false); // corrupt-blob banner
  const currentUrlRef = useRef(null); // owns the blob URL created here
  const touchStartXRef = useRef(null);
  const total = Array.isArray(photos) ? photos.length : 0;
  const current = total > 0 ? photos[Math.max(0, Math.min(index, total - 1))] : null;
  const isLegacy = typeof current === 'string';
  // Stable per-photo key so parent re-renders (fresh `photos` array refs) do
  // NOT re-fire the loader effect → new getPhoto → new object URL thrash.
  const currentPhotoId = useMemo(
    () => (typeof current === 'string' ? current : current?.photoId ?? null),
    [current],
  );

  const revokeOwned = useCallback(() => {
    if (currentUrlRef.current) {
      try { URL.revokeObjectURL(currentUrlRef.current); } catch { /* noop */ }
      currentUrlRef.current = null;
    }
  }, []);

  // Load full blob on demand. Keyed on `currentPhotoId` (see above) so the
  // effect does not re-fire on parent re-render.
  const loadPhoto = useCallback(async (photoId, mountedGetter) => {
    try {
      const result = await store?.getPhoto?.(photoId);
      if (!mountedGetter()) return;
      if (result?.blob) {
        const nextUrl = URL.createObjectURL(result.blob);
        currentUrlRef.current = nextUrl;
        setUrl(nextUrl);
      } else {
        setUrl(null);
      }
    } catch {
      if (mountedGetter()) setUrl(null);
    } finally {
      if (mountedGetter()) setLoading(false);
    }
  }, [store]);

  useEffect(() => {
    // Reset UI state on transitions.
    setLoadError(false);
    if (!isOpen || !currentPhotoId) {
      revokeOwned();
      setUrl(null);
      setLoading(false);
      return undefined;
    }
    if (isLegacy) {
      // currentPhotoId IS the data-URL for legacy string entries.
      revokeOwned();
      setUrl(currentPhotoId);
      setLoading(false);
      return undefined;
    }
    let mounted = true;
    setLoading(true);
    revokeOwned();
    loadPhoto(currentPhotoId, () => mounted);
    return () => {
      mounted = false;
    };
  }, [isOpen, currentPhotoId, isLegacy, revokeOwned, loadPhoto]);

  // Corrupt-blob handling.
  const handleImgError = useCallback(() => setLoadError(true), []);
  const handleRetryLoad = useCallback(() => {
    if (!currentPhotoId || isLegacy) return;
    setLoadError(false);
    setLoading(true);
    revokeOwned();
    let mounted = true;
    loadPhoto(currentPhotoId, () => mounted);
  }, [currentPhotoId, isLegacy, loadPhoto, revokeOwned]);

  // Belt-and-braces cleanup on unmount.
  useEffect(() => () => revokeOwned(), [revokeOwned]);

  const goPrev = useCallback(() => {
    if (total < 2) return;
    onIndexChange?.((index - 1 + total) % total);
  }, [index, total, onIndexChange]);

  const goNext = useCallback(() => {
    if (total < 2) return;
    onIndexChange?.((index + 1) % total);
  }, [index, total, onIndexChange]);

  // Arrow-key navigation
  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') { e.preventDefault(); goPrev(); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); goNext(); }
      else if (e.key === 'Escape') { e.preventDefault(); onClose?.(); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, goPrev, goNext, onClose]);

  // Touch swipe — guard WebKit multi-touch coord garbage AND ignore touches
  // that start/end over inputs/buttons so drags in the caption don't navigate.
  const isSwipeIgnored = (target) =>
    target && typeof target.closest === 'function' && target.closest(SWIPE_IGNORE_SELECTOR);
  const onTouchStart = useCallback((e) => {
    if (isSwipeIgnored(e.target)) { touchStartXRef.current = null; return; }
    const t = e.touches && e.touches[0];
    if (!t) return;
    touchStartXRef.current = t.clientX;
  }, []);
  const onTouchEnd = useCallback((e) => {
    const start = touchStartXRef.current;
    touchStartXRef.current = null;
    if (start == null) return;
    if (isSwipeIgnored(e.target)) return;
    const t = (e.changedTouches && e.changedTouches[0]) || null;
    if (!t) return;
    const dx = t.clientX - start;
    if (Math.abs(dx) < SWIPE_MIN_PX) return;
    if (dx > 0) goPrev(); else goNext();
  }, [goPrev, goNext]);

  const captionValue = !isLegacy ? (current?.caption || '') : '';

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={total > 0 ? `Photo ${Math.min(index + 1, total)} of ${total}` : 'Photo'}
      size="4xl"
    >
      <ModalBody>
        <div
          className="relative bg-black rounded flex items-center justify-center min-h-[50vh] max-h-[70vh] overflow-hidden select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
          data-testid="photo-viewer-body"
        >
          {loading && (
            <div className="absolute inset-0 flex items-center justify-center" data-testid="photo-viewer-loading">
              <Loader2 className="h-10 w-10 text-white animate-spin" aria-hidden />
            </div>
          )}
          {!loading && url && !loadError && (
            <img
              src={url}
              alt={captionValue || `Photo ${index + 1}`}
              className="max-h-[70vh] max-w-full object-contain"
              data-testid="photo-viewer-image"
              draggable={false}
              onError={handleImgError}
            />
          )}
          {!loading && url && loadError && (
            <div
              className="flex flex-col items-center gap-2 text-gray-100 text-sm px-4"
              data-testid="photo-viewer-load-error"
            >
              <AlertTriangle className="h-8 w-8 text-yellow-400" aria-hidden />
              <span>Photo failed to load. Try refreshing.</span>
              {!isLegacy && (
                <button
                  type="button"
                  onClick={handleRetryLoad}
                  data-testid="photo-viewer-retry"
                  data-no-swipe
                  className="mt-1 px-3 py-1 text-xs font-medium text-gray-800 bg-white rounded hover:bg-gray-100"
                >
                  Retry
                </button>
              )}
            </div>
          )}
          {!loading && !url && (
            <div className="text-gray-300 text-sm" data-testid="photo-viewer-missing">
              Photo not available on this device yet.
            </div>
          )}
          {total > 1 && (
            <>
              <button
                type="button"
                onClick={goPrev}
                aria-label="Previous photo"
                data-testid="photo-viewer-prev"
                className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
              >
                <ChevronLeft className="h-6 w-6" />
              </button>
              <button
                type="button"
                onClick={goNext}
                aria-label="Next photo"
                data-testid="photo-viewer-next"
                className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 flex items-center justify-center rounded-full bg-black bg-opacity-50 text-white hover:bg-opacity-70"
              >
                <ChevronRight className="h-6 w-6" />
              </button>
            </>
          )}
        </div>
        {caption && !isLegacy && current && (
          <div className="mt-3">
            <PhotoCaptionInput
              value={captionValue}
              onSave={(next) => onCaptionSave?.(index, next)}
              disabled={disabled}
              testId="photo-viewer-caption"
            />
          </div>
        )}
      </ModalBody>
      <ModalActions>
        <div className="flex justify-between items-center">
          <span className="text-xs text-gray-500" data-testid="photo-viewer-counter">
            {total > 0 ? `${Math.min(index + 1, total)} / ${total}` : ''}
          </span>
          <div className="flex gap-2">
            {/* FT-349d: Replace = delete current + re-capture. Hidden for legacy strings (no photoId). */}
            {onRequestReplace && !disabled && !isLegacy && (
              <button
                type="button"
                onClick={() => onRequestReplace(index)}
                data-testid="photo-viewer-replace"
                data-no-swipe="true"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-cyan-700 bg-white border border-cyan-300 rounded hover:bg-cyan-50"
              >
                <Camera className="h-4 w-4" aria-hidden="true" />
                Replace
              </button>
            )}
            {onRequestDelete && !disabled && (
              <button
                type="button"
                onClick={() => onRequestDelete(index)}
                data-testid="photo-viewer-delete"
                className="inline-flex items-center gap-1 px-3 py-1.5 text-sm font-medium text-red-700 bg-white border border-red-300 rounded hover:bg-red-50"
              >
                <Trash2 className="h-4 w-4" aria-hidden />
                Delete
              </button>
            )}
            <button
              type="button"
              onClick={onClose}
              data-testid="photo-viewer-close"
              className="px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </ModalActions>
    </Modal>
  );
}

export default PhotoViewer;
